import { useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { logoutProcessor } from "./helpers/logoutProcessor";
import { playSuccess, playError } from "./helpers/audioPlayer";
import { ArrowLeft, CheckCircle } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "../i18n";
import HiddenScanInput from "./HiddenScanInput";
import FlowLoader from "./FlowLoader";
import ScanStatusIcons from "./ScanStatusIcons";
import ScanFlashOverlay from "./ScanFlashOverlay";
import FitWord from "./FitWord";
import { conveyorLogger } from "./helpers/logHandler";
import { apiFetch } from "../api/apiFetch";

const CONVEYOR_BARCODE = "conveyor";

type ConveyorPalletValidatorProps = {
    goBack: () => void;
};

type palletType = {
    Date: string;
    Warehouse: string;
    Batch: number;
    Zone: string;
    PalletId: string;
    Collected: boolean;
};

const ConveyorPalletValidator = ({ goBack }: ConveyorPalletValidatorProps) => {
    const navigator = useNavigate();
    const { t } = useTranslation();

    const userData = window.localStorage.getItem("user");
    const userObject = userData !== null ? JSON.parse(userData) : { warehouse: "Newport" };
    const warehouse = userObject.warehouse || "Newport";

    const [step, setStep] = useState<number>(1);
    const [scan, setScan] = useState<string>("");
    const [pallet, setPallet] = useState<palletType | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [scanStatus, set_scanStatus] = useState<"idle" | "success" | "error">("idle");
    const statusTimerRef = useRef<NodeJS.Timeout | null>(null);

    const flashScanStatus = (status: "success" | "error") => {
        if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
        set_scanStatus(status);
        statusTimerRef.current = setTimeout(() => set_scanStatus("idle"), 500);
    };

    const doScanPallet = useCallback(async (scannedValue?: string) => {
        const val = scannedValue ?? scan;
        if (val.trim() === "") return;

        const scannedPalletId = val.trim();
        setScan("");
        setLoading(true);

        try {
            const res = await apiFetch(`/zonestorage/pallets/validate-pallet-data`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    warehouse: warehouse,
                    palletId: scannedPalletId
                })
            });

            if (res.ok) {
                const data: palletType = await res.json();
                if (data.Collected) {
                    flashScanStatus("error");
                    playError();
                    toast.dismiss(); toast.error(t("pallet_already_collected"));
                    conveyorLogger(warehouse, "Validate Sorter Pallet", scannedPalletId, "Warehouse", "Hand", "already collected");
                } else {
                    setPallet(data);
                    setStep(2);
                    conveyorLogger(warehouse, "Validate Sorter Pallet", scannedPalletId, "Warehouse", "Hand", "success");
                }
            } else if (res.status === 401) {
                logoutProcessor();
                navigator("/login");
            } else if (res.status === 404) {
                flashScanStatus("error");
                playError();
                toast.dismiss(); toast.error(t("pallet_not_in_batch"));
                conveyorLogger(warehouse, "Validate Sorter Pallet", scannedPalletId, "Warehouse", "Hand", "not in batch");
            } else {
                flashScanStatus("error");
                playError();
                toast.dismiss(); toast.error(t("pallet_details_failed"));
                conveyorLogger(warehouse, "Validate Sorter Pallet", scannedPalletId, "Warehouse", "Hand", "error");
            }
        } catch {
            flashScanStatus("error");
            playError();
            toast.dismiss(); toast.error(t("pallet_details_failed"));
            conveyorLogger(warehouse, "Validate Sorter Pallet", scannedPalletId, "Warehouse", "Hand", "error");
        }

        setLoading(false);
    }, [scan, warehouse, navigator, t]);

    const doScanConveyor = useCallback(async (scannedValue?: string) => {
        const val = (scannedValue ?? scan).trim();
        if (val === "" || pallet === null) return;

        // Cut warehouse barcode to remove warehouse prefix if present and set to lowercase for comparison
        const conveyorBarcode = val.toLowerCase().slice(3);

        if (conveyorBarcode !== CONVEYOR_BARCODE) {
            playError();
            toast.dismiss(); toast.error(t("wrong_barcode"));
            setScan("");
            conveyorLogger(warehouse, "Collect Sorter Pallet", pallet.PalletId, "Hand", val, "wrong scan");
            return;
        }

        setScan("");
        setSubmitting(true);

        try {
            const res = await apiFetch(`/zonestorage/pallets/mark-pallet-collected`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    warehouse: warehouse,
                    date: pallet.Date,
                    batch: pallet.Batch,
                    palletId: pallet.PalletId
                })
            });

            if (res.ok) {
                const data = await res.json();
                if (data.modifiedCount === 0) {
                    flashScanStatus("error");
                    playError();
                    toast.dismiss(); toast.error(t("pallet_already_collected"));
                    conveyorLogger(warehouse, "Collect Sorter Pallet", pallet.PalletId, "Hand", "Conveyor", "already collected");
                } else {
                    flashScanStatus("success");
                    playSuccess();
                    conveyorLogger(warehouse, "Collect Sorter Pallet", pallet.PalletId, "Hand", "Conveyor", "success");
                }
                setStep(1);
                setPallet(null);
            } else if (res.status === 401) {
                logoutProcessor();
                navigator("/login");
            } else {
                flashScanStatus("error");
                playError();
                toast.dismiss(); toast.error(t("pallet_collect_failed"));
                conveyorLogger(warehouse, "Collect Sorter Pallet", pallet.PalletId, "Hand", "Conveyor", "error");
            }
        } catch {
            flashScanStatus("error");
            playError();
            toast.dismiss(); toast.error(t("pallet_collect_failed"));
            conveyorLogger(warehouse, "Collect Sorter Pallet", pallet.PalletId, "Hand", "Conveyor", "error");
        }

        setSubmitting(false);
    }, [scan, pallet, warehouse, navigator, t]);

    const handleSubmit = useCallback((scannedValue?: string) => {
        if (step === 1) {
            doScanPallet(scannedValue);
        } else {
            doScanConveyor(scannedValue);
        }
    }, [step, doScanPallet, doScanConveyor]);

    return (
        <div className="scan-panel" style={{ backgroundColor: scanStatus === "success" ? "rgba(34, 197, 94, 0.18)" : undefined }}>
            <ScanFlashOverlay active={scanStatus === "error"} />
            <div className="page-title-bar">
                <h1 className="page-title">{t("pallet_validator")}</h1>
                <button className="page-back-btn" onClick={goBack}><ArrowLeft size={20} /></button>
            </div>

            <div className="scan-centre">
                {step === 1 && <ScanStatusIcons status={scanStatus === "error" ? "idle" : scanStatus} />}

                {step === 1 && (
                    <div className="conveyor-scan-group">
                        {loading ? (
                            <FlowLoader />
                        ) : (
                            <div className={`scan-command ${scanStatus !== "idle" ? "scan-command-hidden" : ""}`}>
                                {t("scan_pallet_id").toUpperCase().split(" ").map((word, i) => (
                                    <FitWord key={i}>{word}</FitWord>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {step === 2 && pallet !== null && (
                    <div className="conveyor-scan-group">
                        <div className={`scan-command ${scanStatus === "error" ? "scan-command-hidden" : ""}`}>
                            {t("scan_conveyor").toUpperCase().split(" ").map((word, i) => (
                                <FitWord key={i}>{word}</FitWord>
                            ))}
                        </div>
                        <div className={`scan-feedback-area ${scanStatus === "error" ? "scan-command-hidden" : ""}`}>
                            <p className="conveyor-scan-subtitle">{t("pallet_label", { id: pallet.PalletId })}</p>
                            <p className="conveyor-scan-subtitle-text">{t("scan_conveyor_confirm")}</p>
                        </div>
                        {submitting && <p className="conveyor-status-text">{t("submitting")}</p>}
                    </div>
                )}
            </div>
            {scanStatus === "success" && (
                <div className="s2-inline-status s2-inline-success"><CheckCircle size={140} strokeWidth={3} /></div>
            )}
            <HiddenScanInput
                value={scan}
                onChange={setScan}
                onSubmit={handleSubmit}
                disabled={loading || submitting}
            />
        </div>
    );
};

export default ConveyorPalletValidator;
